/**
 * Header actions on an invoice.
 *
 * A partial or overdue invoice is still an open document: the patient can
 * be sent the PDF, a reminder, or a pay link, and staff can still settle
 * or cancel it. Those buttons used to require status === 'issued', so they
 * vanished as soon as the first payment moved the invoice to 'partial'.
 */

const COLLECTABLE = new Set(['issued', 'partial', 'overdue', 'expired_pay_link']);

export type InvoiceAnnulMode = 'void' | 'cancel';

export interface InvoiceHeaderActions {
  markPaid: boolean;
  payOnline: boolean;
  /** void for draft/issued; cancel once money has moved the status to partial/overdue. */
  annulMode: InvoiceAnnulMode | null;
  downloadPdf: boolean;
  sendEmail: boolean;
  sendReminder: boolean;
  revertPaid: boolean;
  refundOnline: boolean;
  submitToSygeforsikring: boolean;
  /** Credit note for an issued document. Drafts stay on void. */
  credit: boolean;
}

export function isCollectableInvoiceStatus(status: string | null | undefined): boolean {
  return status != null && COLLECTABLE.has(status);
}

export function invoiceHeaderActions(status: string, kind?: string | null): InvoiceHeaderActions {
  if (kind === 'credit_note') {
    return {
      markPaid: false,
      payOnline: false,
      annulMode: null,
      downloadPdf: status !== 'draft',
      sendEmail: false,
      sendReminder: false,
      revertPaid: false,
      refundOnline: false,
      submitToSygeforsikring: false,
      credit: false,
    };
  }
  const collectable = isCollectableInvoiceStatus(status);
  const draft = status === 'draft';
  const paid = status === 'paid';
  const annulMode: InvoiceAnnulMode | null =
    draft || status === 'issued' ? 'void' : collectable ? 'cancel' : null;

  return {
    markPaid: collectable,
    payOnline: draft || collectable,
    annulMode,
    downloadPdf: !draft,
    sendEmail: collectable || paid,
    sendReminder: collectable,
    revertPaid: paid,
    refundOnline: paid || collectable,
    submitToSygeforsikring: status === 'issued',
    credit: paid || collectable,
  };
}

/** Øre to offer as the refund amount. An overpayment suggests the duplicate. */
export function defaultRefundCents(totalCents: number, totalPaidCents: number): number {
  const paid = Math.max(0, totalPaidCents);
  const excess = Math.max(0, paid - Math.max(0, totalCents));
  return excess > 0 ? excess : paid;
}
